import React from 'react';
import { Sprout, Volume2, Compass, HeartHandshake, Users, Lightbulb } from 'lucide-react';

const differentiators = [
  {
    icon: Sprout,
    title: 'We prepare the child first',
    description:
      'Before expecting the child to read, we build the listening, sound awareness, attention, and confidence that real reading is built on.',
    color: 'text-emerald-600 bg-emerald-50'
  },
  {
    icon: Volume2,
    title: 'We teach pure letter sounds — correctly',
    description:
      'No “buh”, “kuh”, or “muh”. Clear, pure sounds so children can blend smoothly and read with confidence from the very beginning.',
    color: 'text-[#3B4BA0] bg-blue-50'
  },
  {
    icon: Compass,
    title: 'Readiness-based, never rushed',
    description:
      'Children are guided according to their individual readiness, pace, and confidence — not the calendar or a one-size-fits-all curriculum.',
    color: 'text-amber-600 bg-amber-50'
  },
  {
    icon: HeartHandshake,
    title: 'We support parents, not just children',
    description:
      'We share simple, practical guidance so parents feel confident helping at home — instead of being left unsure what to do.',
    color: 'text-rose-600 bg-rose-50'
  },
  {
    icon: Users,
    title: 'Practical methods for teachers',
    description:
      'For teachers and aspiring educators, we share child-friendly, classroom-tested techniques you can use the very next day.',
    color: 'text-teal-600 bg-teal-50'
  },
  {
    icon: Lightbulb,
    title: 'We build understanding, not memorisation',
    description:
      'Children develop a real grasp of how sounds and language work, so reading becomes meaningful — not a list of words to memorise.',
    color: 'text-violet-600 bg-violet-50'
  }
];

const WhatMakesDifferent = () => {
  return (
    <section
      id="what-makes-different"
      data-testid="what-makes-different"
      className="py-24 px-4 sm:px-6 lg:px-8 bg-white"
    >
      <div className="max-w-7xl mx-auto">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <div className="inline-block bg-blue-50 text-[#3B4BA0] px-4 py-2 rounded-full text-sm font-semibold mb-4">
            Why Read Right Academy
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 leading-tight">
            What Makes Read Right Academy Different?
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            A child-centred approach built on 20+ years of teaching, Montessori
            training, and patient observation of how young children actually
            learn to read.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {differentiators.map((item, idx) => {
            const Icon = item.icon;
            return (
              <div
                key={idx}
                data-testid={`differentiator-${idx}`}
                className="group relative bg-white rounded-2xl p-7 border-2 border-gray-100 hover:border-gray-200 hover:shadow-xl transition-all duration-300"
              >
                <div
                  className={`w-12 h-12 rounded-xl ${item.color} flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300`}
                >
                  <Icon className="h-6 w-6" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 leading-snug">
                  {item.title}
                </h3>
                <p className="text-gray-600 leading-relaxed text-sm sm:text-base">
                  {item.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default WhatMakesDifferent;
